import React, { useEffect, useMemo, useRef, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

import PropTypes from 'prop-types'

import { MEDICAL_RECORD_SUBTYPE_MAP } from '../../../utils/Constants'
import { getToken } from '../../../utils/getToken'
import MagicWand from '../../common/MagicWand'
import FileMetadataModal from '../../controls/FileMetadataModal'

import { findMatchingEntity } from './entityMatcher'
import ImageHighlightViewer from './ImageHighlightViewer'
import PdfCanvasViewer from './PdfCanvasViewer'
import SourceTextActionPopover from './SourceTextActionPopover'

export default function DocumentViewerPane({
  documents = [],
  entities = [],
  caseNumber = null,
  fileName = 'Document',
  fileType = 'txt',
  textContent = '',
  url = null,
  activeDocumentId,
  onSelectDocument,
  activeSnippet = '',
  activePage = 1,
  activeLineNumber = null,
  activeCategory = '',
  activeEntityField = '',
  activeEntityValue = '',
  onSnippetClick,
  onAddEntity,
  onUpdateDocumentMetadata,
  isAnalyzing = false,
  onClose,
}) {
  const highlightRef = useRef(null)
  const viewerContainerRef = useRef(null)
  const iframeRef = useRef(null)
  const [viewMode, setViewMode] = useState('document') // 'document' | 'paper' | 'text'
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [popoverState, setPopoverState] = useState(null)
  const [showMetadataModal, setShowMetadataModal] = useState(false)

  useEffect(() => {
    if (!isAnalyzing) {
      setElapsedSeconds(0)
      return
    }
    const interval = setInterval(() => {
      setElapsedSeconds(s => s + 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [isAnalyzing])

  const token = typeof window !== 'undefined' ? getToken() : null

  // Normalize documents list for multi-doc support with robust type detection and fallback URL generation
  const normalizedDocs = useMemo(() => {
    const rawList =
      documents && documents.length > 0
        ? documents
        : [
            {
              id: 'doc-1',
              fileName: fileName || 'Document',
              fileType: fileType || 'txt',
              textContent: textContent || '',
              url: url || null,
              pageCount: 1,
              lineBlocks: [],
            },
          ]

    return rawList.map((doc, idx) => {
      const docName = doc.fileName || doc.displayName || `Document ${idx + 1}`
      const ext = docName.includes('.') ? docName.split('.').pop().toLowerCase() : ''
      const isDocPdf = doc.fileType === 'pdf' || ext === 'pdf' || docName.toLowerCase().endsWith('.pdf')
      const isDocImage =
        /^(image|png|jpe?g)$/i.test(doc.fileType || '') || /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i.test(docName)
      const isDocWord = /^(docx?|word)$/i.test(doc.fileType || '') || /\.(docx?)$/i.test(docName)
      const detectedType = isDocPdf ? 'pdf' : isDocImage ? 'image' : isDocWord ? 'docx' : 'txt'

      let resolvedUrl = doc.url || doc.blobUrl || null
      if (!resolvedUrl && (isDocPdf || isDocImage || isDocWord) && docName && caseNumber) {
        resolvedUrl = `/api/cases/${caseNumber}/sharefile-files/content/${encodeURIComponent(docName)}${
          token ? `?access_token=${token}` : ''
        }`
      }

      return {
        ...doc,
        id: doc.id || `doc-${idx + 1}`,
        fileName: docName,
        fileType: detectedType,
        textContent: doc.textContent || doc.rawText || doc.text || '',
        url: resolvedUrl,
        pageCount: doc.pageCount,
        lineBlocks: doc.lineBlocks || [],
      }
    })
  }, [documents, fileName, fileType, textContent, url, caseNumber, token])

  const [selectedDocId, setSelectedDocId] = useState(activeDocumentId || normalizedDocs[0]?.id || 'doc-1')

  // Keep internal selected document in sync when parent changes activeDocumentId
  useEffect(() => {
    if (activeDocumentId && activeDocumentId !== selectedDocId) {
      setSelectedDocId(activeDocumentId)
      setViewMode('document')
    }
  }, [activeDocumentId, selectedDocId])

  const currentDoc = normalizedDocs.find(d => d.id === selectedDocId) || normalizedDocs[0] || {}
  const docPurpose = currentDoc.documentPurpose || currentDoc.purpose || null
  const docSubtype = currentDoc.documentSubtype || currentDoc.subtype || null
  const docSequenceNumber = currentDoc.sequenceNumber || currentDoc.parsedProperties?.['Sequence Number'] || null
  const docStartDate = currentDoc.startDate || currentDoc.parsedProperties?.['Start Date'] || null
  const docEndDate = currentDoc.endDate || currentDoc.parsedProperties?.['End Date'] || null
  const isCaseReportDoc = ['Initial Case Report', 'Case Management Progress Report', 'Final Case Report'].includes(
    docPurpose,
  )
  const lines = useMemo(() => (currentDoc.textContent || '').split('\n'), [currentDoc.textContent])

  // Parse natural paragraphs and headings for clean Word & Text Document view (without row numbers)
  const paragraphs = useMemo(() => {
    const rawLines = (currentDoc.textContent || '').split('\n')
    const result = []
    let currentPara = []

    for (let i = 0; i < rawLines.length; i++) {
      const line = rawLines[i]
      const trimmed = line.trim()
      if (!trimmed) {
        if (currentPara.length > 0) {
          result.push({ text: currentPara.join(' '), isHeading: false })
          currentPara = []
        }
      } else {
        const isHeading =
          trimmed.length < 70 &&
          ((trimmed === trimmed.toUpperCase() && /[A-Z]/.test(trimmed) && trimmed.length > 3) ||
            trimmed.endsWith(':') ||
            /^(section|chapter|part|re:|subject:|patient|diagnosis|history|summary|treatment|employer|claimant|physician|assessment|findings):/i.test(
              trimmed,
            ))

        if (isHeading) {
          if (currentPara.length > 0) {
            result.push({ text: currentPara.join(' '), isHeading: false })
            currentPara = []
          }
          result.push({ text: trimmed, isHeading: true })
        } else {
          currentPara.push(trimmed)
        }
      }
    }
    if (currentPara.length > 0) {
      result.push({ text: currentPara.join(' '), isHeading: false })
    }
    return result.length > 0
      ? result
      : currentDoc.textContent
      ? [{ text: currentDoc.textContent, isHeading: false }]
      : []
  }, [currentDoc.textContent])

  const isInjuryDateField =
    (activeEntityField || '').toLowerCase().includes('injury') &&
    ((activeEntityField || '').toLowerCase().includes('date') || (activeCategory || '').toLowerCase().includes('date'))

  const isSnippetInLine = (line, idx) => {
    if (!line) return false
    const cleanLine = line.trim().toLowerCase()
    if (cleanLine.length < 2) return false

    const isCompetingNotificationLine =
      isInjuryDateField &&
      /\b(?:notified|employer\s+notified|date\s+employer)\b/i.test(cleanLine) &&
      !/\b(?:injury|accident|illness)\b/i.test(cleanLine)

    if (isCompetingNotificationLine) {
      return false
    }

    if (activeLineNumber && idx !== undefined && idx + 1 === activeLineNumber) {
      return true
    }

    if (activeSnippet) {
      const cleanSnippet = activeSnippet.trim().toLowerCase()
      if (cleanLine.includes(cleanSnippet) || (cleanSnippet.length > 15 && cleanSnippet.includes(cleanLine))) {
        return true
      }
      // Check semicolon, period, or comma clauses
      const clauses = cleanSnippet
        .split(/[;.,]/)
        .map(c => c.trim())
        .filter(c => c.length >= 6)
      for (const clause of clauses) {
        if (cleanLine.includes(clause) || (clause.length > 10 && clause.includes(cleanLine))) {
          return true
        }
      }
    }

    if (activeEntityValue && typeof activeEntityValue === 'string' && activeEntityValue.trim().length >= 4) {
      const cleanVal = activeEntityValue.trim().toLowerCase()
      if (cleanLine.includes(cleanVal)) {
        return true
      }
    }

    return false
  }

  // Auto-scroll to active snippet when activeSnippet or activeEntity changes, strictly confined to viewerContainerRef
  useEffect(() => {
    if (highlightRef.current && viewerContainerRef.current) {
      const container = viewerContainerRef.current
      const targetEl = highlightRef.current
      const containerRect = container.getBoundingClientRect()
      const targetRect = targetEl.getBoundingClientRect()
      const relativeTop = targetRect.top - containerRect.top + container.scrollTop
      const targetScrollTop = Math.max(0, relativeTop - container.clientHeight / 2 + targetRect.height / 2)

      if (typeof container.scrollTo === 'function') {
        container.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth',
        })
      } else {
        container.scrollTop = targetScrollTop
      }
    }
  }, [activeSnippet, activePage, selectedDocId, viewMode, activeEntityValue])

  const handleTabClick = docId => {
    setSelectedDocId(docId)
    setViewMode('document')
    if (onSelectDocument) {
      onSelectDocument(docId)
    }
  }

  const isWordDoc =
    currentDoc.fileType === 'docx' ||
    currentDoc.fileType === 'doc' ||
    currentDoc.fileType === 'word' ||
    currentDoc.fileName?.toLowerCase().endsWith('.docx') ||
    currentDoc.fileName?.toLowerCase().endsWith('.doc')
  const isPdf = currentDoc.fileType === 'pdf' || currentDoc.fileName?.toLowerCase().endsWith('.pdf')
  const isImage =
    currentDoc.fileType === 'image' ||
    currentDoc.fileType === 'png' ||
    currentDoc.fileType === 'jpg' ||
    currentDoc.fileType === 'jpeg' ||
    /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i.test(currentDoc.fileName || '')
  const isTxt = !isPdf && !isWordDoc && !isImage

  // Construct standard PDF URL with page anchor and zoom level for native PDF viewer
  const pdfUrlWithPage = useMemo(() => {
    if (!currentDoc.url) return null
    return `${currentDoc.url}#page=${activePage || 1}&zoom=page-width`
  }, [currentDoc.url, activePage])

  // Navigate PDF page in-place without destroying the iframe DOM node if same page
  useEffect(() => {
    if (iframeRef.current && pdfUrlWithPage) {
      try {
        if (iframeRef.current.contentWindow?.location?.href) {
          const curHref = iframeRef.current.contentWindow.location.href
          const baseCur = curHref.split('#')[0]
          const baseNew = pdfUrlWithPage.split('#')[0]
          if (baseCur === baseNew) {
            iframeRef.current.contentWindow.location.replace(pdfUrlWithPage)
          }
        }
      } catch (e) {
        // Sandboxed or cross-origin fallback: let React key remount handle page changes
      }
    }
  }, [pdfUrlWithPage])

  const handlePaperMouseUp = () => {
    if (typeof window === 'undefined') return
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed) return
    const text = selection.toString().trim()
    if (text.length < 2) return

    try {
      const range = selection.getRangeAt(0)
      const rect = range.getBoundingClientRect()

      const matched = findMatchingEntity(text, entities)

      setPopoverState({
        text,
        sourceSnippet: text,
        page: activePage || 1,
        documentId: currentDoc.id,
        coords: {
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        },
        matchedEntity: matched || null,
      })
    } catch {}
  }

  const handlePaperLineClick = (e, line) => {
    const selection = window.getSelection()
    if (selection && !selection.isCollapsed && selection.toString().trim().length >= 2) {
      return
    }
    const cleanL = String(line || '')
      .toLowerCase()
      .trim()
    if (!cleanL) return

    const matched = findMatchingEntity(line, entities)

    if (matched && onSnippetClick) {
      onSnippetClick(matched.extractedValue || matched.field, matched.id)
    } else {
      const rect = e.currentTarget.getBoundingClientRect()
      setPopoverState({
        text: line.trim(),
        sourceSnippet: line.trim(),
        page: activePage || 1,
        documentId: currentDoc.id,
        coords: {
          x: rect.left + 40,
          y: rect.top,
          width: Math.min(rect.width, 260),
          height: rect.height,
        },
        matchedEntity: null,
      })
    }
  }

  return (
    <div className="hitl-left-pane position-relative">
      {/* Analyzing Overlay */}
      {isAnalyzing && (
        <div className="hitl-analyzing-overlay">
          <div className="card hitl-analyzing-card text-center p-4 border-info shadow">
            <div
              className="spinner-border text-info mx-auto mb-3"
              style={{ width: '3rem', height: '3rem' }}
              role="status"
            >
              <span className="sr-only">Analyzing...</span>
            </div>
            <h5 className="font-weight-bold text-dark mb-2 d-flex align-items-center justify-content-center">
              <span
                style={{
                  width: '20px',
                  height: '20px',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  marginRight: '8px',
                  flexShrink: 0,
                }}
              >
                <MagicWand active={true} />
              </span>
              Smart Upload in progress
            </h5>
            <div className="mb-3">
              <span
                className="badge badge-info font-weight-bold px-3 py-2 text-white shadow-sm"
                style={{ fontSize: '0.88rem', letterSpacing: '0.5px' }}
              >
                <i className="far fa-clock mr-1" />
                Elapsed: {Math.floor(elapsedSeconds / 60)}m {String(elapsedSeconds % 60).padStart(2, '0')}s
              </span>
            </div>
            <p className="text-muted small mb-3">
              {elapsedSeconds >= 90
                ? 'Processing large document record. Comprehensive records with dozens or hundreds of pages (e.g. 30MB+ scans) may take 15–20 minutes. Your documents are securely saved to the case.'
                : 'GuideSmart is analyzing your document with AI — extracting patient demographics, treatment providers, clinical diagnoses, and locating source pages...'}
            </p>
            {onClose && (
              <div className="mt-2 pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary font-weight-bold shadow-sm"
                  onClick={onClose}
                  title="Your documents are saved in the case. Extractions will be staged for review when ready."
                >
                  <i className="fas fa-compress-alt mr-1" /> Continue Working in Background
                </button>
                <div className="small text-muted mt-2" style={{ fontSize: '0.75rem' }}>
                  Your documents are securely stored in the case. You can close this window at any time.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Multi-Document Tab Bar */}
      {normalizedDocs.length > 1 && (
        <div className="hitl-document-tab-bar">
          <div className="d-flex align-items-center" style={{ gap: '4px', overflowX: 'auto', padding: '4px 8px' }}>
            <span className="small text-muted font-weight-bold mr-2" style={{ fontSize: '0.75rem', flexShrink: 0 }}>
              <i className="fas fa-layer-group mr-1" /> Source Docs ({normalizedDocs.length}):
            </span>
            {normalizedDocs.map((doc, idx) => {
              const isActive = doc.id === currentDoc.id
              return (
                <button
                  key={doc.id}
                  type="button"
                  className={`btn btn-xs hitl-doc-tab-btn ${
                    isActive ? 'btn-primary font-weight-bold' : 'btn-outline-secondary'
                  }`}
                  onClick={() => handleTabClick(doc.id)}
                  title={`View ${doc.fileName}`}
                  style={{
                    fontSize: '0.75rem',
                    padding: '0.2rem 0.5rem',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    borderWidth: '1px',
                  }}
                >
                  <i
                    className={`mr-1 ${
                      doc.fileType === 'pdf'
                        ? 'fas fa-file-pdf'
                        : doc.fileType === 'docx'
                        ? 'fas fa-file-word'
                        : doc.fileType === 'image' || /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i.test(doc.fileName || '')
                        ? 'fas fa-file-image'
                        : 'fas fa-file-alt'
                    }`}
                  />
                  {idx + 1}. {doc.fileName.length > 22 ? `${doc.fileName.slice(0, 20)}…` : doc.fileName}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Header Bar */}
      <div className="hitl-viewer-header">
        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
          <i
            className={`fa-lg ${
              isWordDoc
                ? 'fas fa-file-word text-primary'
                : isPdf
                ? 'fas fa-file-pdf text-danger'
                : isImage
                ? 'fas fa-file-image text-info'
                : 'fas fa-file-alt text-secondary'
            }`}
          />
          <div>
            <div className="d-flex align-items-center flex-wrap" style={{ gap: '6px' }}>
              <span className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                {currentDoc.fileName}
              </span>
              {docPurpose && (
                <span className="badge badge-info badge-pill" style={{ fontSize: '0.72rem' }}>
                  {docPurpose}
                </span>
              )}
              {docPurpose === 'Case Management Progress Report' && docSequenceNumber && (
                <span className="badge badge-secondary badge-pill" style={{ fontSize: '0.72rem' }}>
                  Report #{docSequenceNumber}
                </span>
              )}
              {isCaseReportDoc && (docStartDate || docEndDate) && (
                <span className="badge badge-light border badge-pill text-dark" style={{ fontSize: '0.72rem' }}>
                  <i className="far fa-calendar-alt mr-1" />
                  {String(docStartDate || '…').substring(0, 10)} – {String(docEndDate || '…').substring(0, 10)}
                </span>
              )}
              {docSubtype && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id={`viewer-subtype-tt-${currentDoc.id}`}>
                      {MEDICAL_RECORD_SUBTYPE_MAP[docSubtype] || docSubtype}
                    </Tooltip>
                  }
                >
                  <span className="badge badge-primary badge-pill" style={{ fontSize: '0.72rem', cursor: 'help' }}>
                    <i className="fas fa-tag mr-1" />
                    {docSubtype}
                  </span>
                </OverlayTrigger>
              )}
              {onUpdateDocumentMetadata && currentDoc.fileName && (
                <button
                  type="button"
                  className="btn btn-link btn-xs p-0 ml-1 text-secondary"
                  onClick={() => setShowMetadataModal(true)}
                  title="Edit Document / Report Properties"
                  style={{ fontSize: '0.78rem' }}
                >
                  <i className="fas fa-pencil-alt" />
                </button>
              )}
            </div>
            <small className="text-muted">
              {isWordDoc ? 'Word Document' : isPdf ? 'PDF Record' : isImage ? 'Image Document' : 'Text Document'}
              {activePage ? ` • Page ${activePage}` : ''}
              {currentDoc.textContent ? ` • ${lines.length} lines parsed` : ''}
            </small>
          </div>
        </div>

        {/* View Mode Toggle & Legend */}
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn btn-xs ${
                viewMode === 'document' ? 'btn-primary font-weight-bold' : 'btn-outline-secondary'
              }`}
              onClick={() => setViewMode('document')}
              style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem' }}
            >
              <i
                className={`mr-1 ${
                  isPdf
                    ? 'fas fa-file-pdf'
                    : isImage
                    ? 'fas fa-file-image'
                    : isWordDoc
                    ? 'fas fa-file-word'
                    : 'fas fa-file-alt'
                }`}
              />
              {isPdf && currentDoc.url
                ? 'PDF View'
                : isImage && currentDoc.url
                ? 'Image View'
                : isWordDoc
                ? 'Word Document'
                : isTxt
                ? 'Text Document'
                : 'Document View'}
            </button>
            {(isPdf || isImage) && currentDoc.textContent && (
              <button
                type="button"
                className={`btn btn-xs ${
                  viewMode === 'paper' ? 'btn-primary font-weight-bold' : 'btn-outline-secondary'
                }`}
                onClick={() => setViewMode('paper')}
                style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem' }}
                title="View formatted OCR document stream with yellow line highlights"
              >
                <i className="fas fa-align-left mr-1" />
                OCR Text Stream
              </button>
            )}
            {isPdf && pdfUrlWithPage && (
              <button
                type="button"
                className={`btn btn-xs ${
                  viewMode === 'iframe' ? 'btn-primary font-weight-bold' : 'btn-outline-secondary'
                }`}
                onClick={() => setViewMode('iframe')}
                style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem' }}
                title="View raw PDF in native browser iframe"
              >
                <i className="fas fa-window-maximize mr-1" />
                Browser Frame
              </button>
            )}
          </div>

          <span className="badge badge-light border d-none d-md-inline-block">
            <span
              style={{
                display: 'inline-block',
                width: '10px',
                height: '10px',
                backgroundColor: '#ffc107',
                borderRadius: '2px',
                marginRight: '4px',
              }}
            />
            Highlighted Source
          </span>
        </div>
      </div>

      {/* Active Source Reference Bar */}
      {(activeSnippet || activePage || activeEntityField) && (
        <div
          className="hitl-source-reference-bar px-3 py-2 border-bottom d-flex align-items-center justify-content-between"
          style={{ borderLeft: '4px solid #ffc107', backgroundColor: '#fff9db', fontSize: '0.82rem' }}
        >
          <div className="d-flex align-items-center text-truncate mr-2" style={{ gap: '8px' }}>
            <span className="badge badge-warning text-dark font-weight-bold" style={{ flexShrink: 0 }}>
              <i className="fas fa-search-location mr-1" />
              {activePage ? `Page ${activePage}` : 'Source'}
              {activeLineNumber ? ` • Line ${activeLineNumber}` : ''}
            </span>
            {activeEntityField && (
              <span className="text-muted small mr-1 font-weight-bold" style={{ flexShrink: 0 }}>
                {activeEntityField}:
              </span>
            )}
            {activeSnippet ? (
              <span className="text-dark font-italic text-truncate" title={activeSnippet}>
                &ldquo;{activeSnippet}&rdquo;
              </span>
            ) : activeEntityValue ? (
              <span className="text-muted small text-truncate">
                Jumped PDF to Page {activePage || 1} for &ldquo;{activeEntityValue}&rdquo;
              </span>
            ) : null}
          </div>
          <div className="d-flex align-items-center" style={{ gap: '6px', flexShrink: 0 }}>
            {viewMode !== 'paper' && (isPdf || isImage) && currentDoc.textContent && (
              <button
                type="button"
                className="btn btn-xs btn-outline-warning text-dark font-weight-bold"
                onClick={() => setViewMode('paper')}
                style={{ fontSize: '0.72rem', padding: '0.2rem 0.55rem', whiteSpace: 'nowrap' }}
                title="View document in plain text stream"
              >
                <i className="fas fa-align-left mr-1 text-dark" /> Text Stream
              </button>
            )}
            {viewMode !== 'document' && isPdf && currentDoc.url && (
              <button
                type="button"
                className="btn btn-xs btn-primary font-weight-bold shadow-sm"
                onClick={() => setViewMode('document')}
                style={{ fontSize: '0.72rem', padding: '0.2rem 0.55rem', whiteSpace: 'nowrap' }}
                title="Switch back to PDF Canvas viewer"
              >
                <i className="fas fa-file-pdf mr-1" /> PDF View
              </button>
            )}
            {viewMode !== 'document' && isImage && currentDoc.url && (
              <button
                type="button"
                className="btn btn-xs btn-info text-white font-weight-bold shadow-sm"
                onClick={() => setViewMode('document')}
                style={{ fontSize: '0.72rem', padding: '0.2rem 0.55rem', whiteSpace: 'nowrap' }}
                title="Switch back to Image View"
              >
                <i className="fas fa-file-image mr-1" /> Image View
              </button>
            )}
          </div>
        </div>
      )}

      {/* Document Content View */}
      {viewMode === 'document' && isPdf && currentDoc.url ? (
        <PdfCanvasViewer
          url={currentDoc.url}
          fileName={currentDoc.fileName}
          lineBlocks={currentDoc.lineBlocks || []}
          activePage={activePage || 1}
          activeLineNumber={activeLineNumber}
          activeSnippet={activeSnippet}
          activeCategory={activeCategory}
          activeEntityField={activeEntityField}
          activeEntityValue={activeEntityValue}
          entities={entities}
          onSnippetClick={onSnippetClick}
          onShowPopover={setPopoverState}
        />
      ) : viewMode === 'document' && isImage && currentDoc.url ? (
        <ImageHighlightViewer
          url={currentDoc.url}
          fileName={currentDoc.fileName}
          lineBlocks={currentDoc.lineBlocks || []}
          activePage={activePage || 1}
          activeLineNumber={activeLineNumber}
          activeSnippet={activeSnippet}
          activeCategory={activeCategory}
          activeEntityField={activeEntityField}
          activeEntityValue={activeEntityValue}
          entities={entities}
          onSnippetClick={onSnippetClick}
          onShowPopover={setPopoverState}
        />
      ) : viewMode === 'iframe' && isPdf && pdfUrlWithPage ? (
        <div className="hitl-pdf-container">
          <iframe
            ref={iframeRef}
            key={currentDoc.id}
            className="hitl-pdf-frame"
            src={pdfUrlWithPage}
            title={currentDoc.fileName}
          />
        </div>
      ) : viewMode === 'document' && isWordDoc ? (
        /* Dedicated Word Document View with authentic page styling & NO line numbers */
        /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
        <div ref={viewerContainerRef} className="hitl-word-document-container" onMouseUp={handlePaperMouseUp}>
          <div className="hitl-word-document-page">
            <div className="hitl-word-doc-banner">
              <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                <i className="fas fa-file-word fa-2x text-primary" />
                <div>
                  <div className="doc-title">{currentDoc.fileName}</div>
                  <div className="text-muted small">
                    {docPurpose ? (
                      <span className="mr-2">
                        <strong>Purpose:</strong> {docPurpose}
                      </span>
                    ) : null}
                    {docSubtype ? (
                      <span>
                        <strong>Type:</strong> {docSubtype}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
              <span className="badge badge-primary px-3 py-2 font-weight-bold" style={{ fontSize: '0.78rem' }}>
                <i className="fas fa-file-word mr-1" /> Word Document View
              </span>
            </div>

            {paragraphs.length > 0 ? (
              paragraphs.map((p, idx) => {
                const isHighlighted = isSnippetInLine(p.text, idx)
                return (
                  <div
                    key={idx}
                    ref={isHighlighted ? highlightRef : null}
                    className={`hitl-word-paragraph ${p.isHeading ? 'heading' : ''} ${
                      isHighlighted ? 'highlighted' : ''
                    }`}
                    onClick={e => handlePaperLineClick(e, p.text)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handlePaperLineClick(e, p.text)
                    }}
                  >
                    <span>{p.text}</span>
                  </div>
                )
              })
            ) : (
              <div className="text-center text-muted my-5 p-4">
                <i className="fas fa-file-word fa-3x mb-3 text-muted" />
                <p>No document content available for this Word document.</p>
              </div>
            )}
          </div>
        </div>
      ) : viewMode === 'document' && isTxt ? (
        /* Dedicated Text Document View with clean document styling & NO line numbers */
        /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
        <div ref={viewerContainerRef} className="hitl-text-document-container" onMouseUp={handlePaperMouseUp}>
          <div className="hitl-text-document-page">
            <div className="d-flex justify-content-between align-items-center pb-3 mb-4 border-bottom">
              <div>
                <strong style={{ fontSize: '1.05rem', color: '#334155' }}>{currentDoc.fileName}</strong>
                {docPurpose && <span className="ml-2 text-muted small">• {docPurpose}</span>}
              </div>
              <span className="badge badge-secondary px-2 py-1 font-weight-bold">
                <i className="fas fa-file-alt mr-1" /> Text Document View
              </span>
            </div>

            {paragraphs.length > 0 ? (
              paragraphs.map((p, idx) => {
                const isHighlighted = isSnippetInLine(p.text, idx)
                return (
                  <div
                    key={idx}
                    ref={isHighlighted ? highlightRef : null}
                    className={`hitl-text-paragraph ${isHighlighted ? 'highlighted' : ''}`}
                    onClick={e => handlePaperLineClick(e, p.text)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handlePaperLineClick(e, p.text)
                    }}
                  >
                    <span>{p.text}</span>
                  </div>
                )
              })
            ) : (
              <div className="text-center text-muted my-5 p-4">
                <i className="fas fa-file-alt fa-3x mb-3 text-muted" />
                <p>No document text stream available for preview.</p>
              </div>
            )}
          </div>
        </div>
      ) : viewMode === 'document' && (isPdf || isImage) ? (
        /* PDF/Image fallback when URL could not be resolved */
        <div ref={viewerContainerRef} className="hitl-paper-document-container">
          <div className="alert alert-info my-5 mx-auto p-4 text-center" style={{ maxWidth: '600px' }}>
            <i className={`${isPdf ? 'fas fa-file-pdf text-danger' : 'fas fa-file-image text-info'} fa-3x mb-3`} />
            <h5>Preparing Document Visual Preview</h5>
            <p className="small text-muted mb-3">
              The high-resolution visual file is being prepared or synchronized. You can review the extracted clinical
              text stream in the meantime.
            </p>
            {currentDoc.textContent && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm font-weight-bold"
                onClick={() => setViewMode('paper')}
              >
                <i className="fas fa-align-left mr-1" /> View OCR Text Stream
              </button>
            )}
          </div>
        </div>
      ) : viewMode === 'paper' ? (
        /* OCR Text Stream strictly when viewMode === 'paper' */
        /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
        <div ref={viewerContainerRef} className="hitl-paper-document-container" onMouseUp={handlePaperMouseUp}>
          <div className="hitl-paper-document">
            <div className="text-center pb-3 mb-3 border-bottom text-muted small">
              <strong>{currentDoc.fileName}</strong>
              <span className="badge badge-warning ml-2 text-dark font-weight-bold">
                <i className="fas fa-highlighter mr-1" /> OCR Text Stream (Numbered)
              </span>
              {activePage ? <span className="ml-2">• Page {activePage}</span> : ''}
            </div>
            {lines.length > 0 && currentDoc.textContent ? (
              lines.map((line, idx) => {
                const isHighlighted = isSnippetInLine(line, idx)
                return (
                  <div
                    key={idx}
                    ref={isHighlighted ? highlightRef : null}
                    className={`paper-line ${isHighlighted ? 'highlighted' : ''}`}
                    onClick={e => handlePaperLineClick(e, line)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handlePaperLineClick(e, line)
                    }}
                  >
                    <span
                      className="text-muted mr-3 small"
                      style={{ userSelect: 'none', minWidth: '35px', display: 'inline-block', textAlign: 'right' }}
                    >
                      {idx + 1}
                    </span>
                    <span>{line || '\u00A0'}</span>
                  </div>
                )
              })
            ) : (
              <div className="text-center text-muted my-5 p-4">
                <i className="fas fa-file-alt fa-3x mb-3 text-muted" />
                <p>No document text stream available for preview.</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Extracted Monospace Text View */
        /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
        <div ref={viewerContainerRef} className="hitl-viewer-content" onMouseUp={handlePaperMouseUp}>
          {lines.length > 0 && currentDoc.textContent ? (
            lines.map((line, idx) => {
              const isHighlighted = isSnippetInLine(line, idx)
              return (
                <div
                  key={idx}
                  ref={isHighlighted ? highlightRef : null}
                  className={`text-line ${isHighlighted ? 'highlighted' : ''}`}
                  onClick={e => handlePaperLineClick(e, line)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handlePaperLineClick(e, line)
                  }}
                >
                  <span className="line-number">{idx + 1}</span>
                  <span className="line-content">{line || ' '}</span>
                </div>
              )
            })
          ) : (
            <div className="text-center text-muted my-5 p-4">
              <i className="fas fa-file-alt fa-3x mb-3 text-muted" />
              <p>No document text stream available for preview.</p>
            </div>
          )}
        </div>
      )}

      {/* Floating Action Popover for Copy, Sync, and Add-to-Case */}
      {popoverState && (
        <SourceTextActionPopover
          {...popoverState}
          onAddEntity={onAddEntity}
          onViewMatchedEntity={entId => {
            if (onSnippetClick) onSnippetClick(popoverState.text, entId)
            setPopoverState(null)
          }}
          onClose={() => setPopoverState(null)}
        />
      )}

      {showMetadataModal && onUpdateDocumentMetadata && (
        <FileMetadataModal
          show={showMetadataModal}
          onHide={() => setShowMetadataModal(false)}
          title={`Edit Properties: ${currentDoc.fileName || 'Document'}`}
          confirmText="Save Changes"
          defaultValues={{
            ...(currentDoc.parsedProperties || {}),
            'Document Purpose': docPurpose || 'Medical Record',
            'Document Subtype': docSubtype || '',
            'Sequence Number': docSequenceNumber || '',
            'Start Date': docStartDate || '',
            'End Date': docEndDate || '',
          }}
          onConfirm={updatedMeta => {
            setShowMetadataModal(false)
            onUpdateDocumentMetadata(currentDoc, updatedMeta)
          }}
        />
      )}
    </div>
  )
}

DocumentViewerPane.propTypes = {
  documents: PropTypes.arrayOf(PropTypes.object),
  entities: PropTypes.arrayOf(PropTypes.object),
  caseNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fileName: PropTypes.string,
  fileType: PropTypes.string,
  textContent: PropTypes.string,
  url: PropTypes.string,
  activeDocumentId: PropTypes.string,
  onSelectDocument: PropTypes.func,
  activeSnippet: PropTypes.string,
  activePage: PropTypes.number,
  activeLineNumber: PropTypes.number,
  activeCategory: PropTypes.string,
  activeEntityField: PropTypes.string,
  activeEntityValue: PropTypes.string,
  onSnippetClick: PropTypes.func,
  onAddEntity: PropTypes.func,
  onUpdateDocumentMetadata: PropTypes.func,
  isAnalyzing: PropTypes.bool,
  onClose: PropTypes.func,
}
